export const normalizeMfeBundleFee = (fee, metadata = {}) => {
    const numericFee = Number(fee)
    if (Number.isNaN(numericFee)) return fee

    const unit = String(metadata?.unit || metadata?.amountUnit || '').toLowerCase()
    if (unit === 'cent' || unit === 'cents' || unit === 'minor') return numericFee / 100
    if (unit === 'dollar' || unit === 'dollars' || unit === 'major') return numericFee
    if (metadata?.isMinorUnit === true) return numericFee / 100
    if (metadata?.isMinorUnit === false) return numericFee

    // Host summary math expects dollar amounts. MFE can return cents for promo
    // bundle fees (e.g. -500 for -$5.00).
    // NOTE: This fallback heuristic is intentionally conservative and still has
    // known edge cases when the upstream contract omits units (e.g. 150 could
    // mean either $150.00 or $1.50). Prefer explicit unit/isMinorUnit metadata
    // from the MFE payload whenever available.
    return Math.abs(numericFee) >= 100 ? numericFee / 100 : numericFee
}

export const looksLikeUuid = (value) => {
    return typeof value === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

export const resolvePricePlanGuid = (mfePlan) => {
    const candidates = [
        mfePlan?.commerce?.pricePlanGuid,
        mfePlan?.pricePlanGuid,
        mfePlan?.guid,
        mfePlan?.commerce?.guid
    ]

    const uuidGuid = candidates.find((candidate) => looksLikeUuid(candidate))
    if (uuidGuid) return uuidGuid

    return candidates.find(Boolean)
}

export const featureTextFromUnknown = (value) => {
    if (!value) return ''
    if (typeof value === 'string') return value.trim()

    if (typeof value === 'object') {
        const candidate = value?.text
            || value?.name
            || value?.label
            || value?.title
            || value?.value
            || value?.description
        return typeof candidate === 'string' ? candidate.trim() : ''
    }

    return ''
}

export const resolveMfeFeatures = (mfePlan) => {
    const rawFeatures = [
        ...(Array.isArray(mfePlan?.features) ? mfePlan.features : []),
        ...(Array.isArray(mfePlan?.commerce?.features) ? mfePlan.commerce.features : []),
        ...(Array.isArray(mfePlan?.commerce?.options) ? mfePlan.commerce.options : [])
    ]

    const normalized = rawFeatures
        .map((feature) => featureTextFromUnknown(feature))
        .filter(Boolean)

    if (normalized.length > 0) return Array.from(new Set(normalized))

    const allowanceData = featureTextFromUnknown(mfePlan?.allowances?.data)
    const speedName = featureTextFromUnknown(mfePlan?.name)
    if (allowanceData && speedName) return [`${allowanceData} at ${speedName}`]
    if (allowanceData) return [allowanceData]

    return []
}

export const resolveGuidFromCatalog = (localizedPlanItem, lang, planId) => {
    if (!localizedPlanItem || !lang || planId == null) return undefined

    const selectedId = String(planId)
    const normalizedBuckets = []

    const pushBuckets = (value) => {
        if (Array.isArray(value)) {
            normalizedBuckets.push(...value)
            return
        }
        if (value && typeof value === 'object') {
            normalizedBuckets.push(value)
        }
    }

    // Prefer current language first.
    pushBuckets(localizedPlanItem?.[lang])

    // Fallback across all language buckets (helps on back/forward race
    // conditions where one locale bucket is not yet hydrated at click time).
    Object.values(localizedPlanItem || {}).forEach((bucket) => {
        pushBuckets(bucket)
    })

    const guidMatches = []
    for (let i = 0; i < normalizedBuckets.length; i += 1) {
        const plans = normalizedBuckets[i]?.plans
        if (Array.isArray(plans)) {
            const match = plans.find((catalogPlan) => String(catalogPlan?.id) === selectedId)
            if (match?.guid) guidMatches.push(match.guid)
        }
    }

    const uuidGuid = guidMatches.find((guid) => looksLikeUuid(guid))
    if (uuidGuid) return uuidGuid

    return guidMatches.find(Boolean)
}

/**
 * Adapts a PlanCardVM returned by the MFE onSelect callback to the host's
 * normalised plan shape. planId is the canonical id from
 * onSelect(planId, mfePlan); when unavailable the id is resolved from the plan
 * payload itself.
 */
export const mfePlanToHostPlan = (mfePlan, lang, planId) => {
    const rawPromo = mfePlan?.commerce?.promotions?.[0]
    const promo = rawPromo
        ? {
            ...rawPromo,
            bundles: Array.isArray(rawPromo?.bundles)
                // `name` (EN) and `namefr` (FR) are both kept so the order
                // summary can re-word on a language switch without re-fetching
                // the promo. Only the fee is normalised here.
                ? rawPromo.bundles.map((bundle) => ({
                    ...bundle,
                    fee: normalizeMfeBundleFee(bundle?.fee, bundle)
                }))
                : rawPromo?.bundles
        }
        : undefined
    const promoText =
        lang === 'fr' && promo?.bundleName ? promo.bundleName : promo?.text

    const id =
        planId
        ?? mfePlan?.commerce?.id
        ?? mfePlan?.commerce?.planId
        ?? mfePlan?.commerce?.pricePlanId
        ?? mfePlan?.id
    const guid = resolvePricePlanGuid(mfePlan)
    const features = resolveMfeFeatures(mfePlan)

    return {
        id,
        guid,
        name: mfePlan?.name,
        subtitle: mfePlan?.subtitle,
        sluggedName: mfePlan?.commerce?.sluggedName ?? mfePlan?.sluggedName,
        value: mfePlan?.price ?? mfePlan?.commerce?.price ?? mfePlan?.commerce?.cost,
        duration: mfePlan?.numOfDays ?? mfePlan?.duration,
        promo: promo ? { ...promo, text: promoText } : undefined,
        options: mfePlan?.commerce?.options,
        features,
        recommended: mfePlan?.markedRecommended,
        planData: mfePlan?.allowances?.data,
        description: mfePlan?.description,
        planRibbonContent: mfePlan?.ribbon?.text,
        extraOffer: promoText,
        infoText: mfePlan?.perkTitle,
        province: mfePlan?.commerce?.province ?? mfePlan?.province,
        commerce: mfePlan?.commerce,
        pricePlanGuid: guid
    }
}